import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import axios from "axios";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { LAMBDA_BASE_URL } from "../../constants";
import { ZohoAccount } from "../../types/zoho";

interface Props {
  selectedAccount: ZohoAccount | null;
  onChange: (account: ZohoAccount | null) => void;
  accountNameFromEmail: string | null;
}

export const NEW_ACCOUNT_ID = "new";

const AccountSearchBox: React.FC<Props> = ({
  onChange,
  accountNameFromEmail,
  selectedAccount,
}) => {
  const [results, setResults] = useState<ZohoAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState<string>(
    accountNameFromEmail || ""
  );
  console.log("selectedAccount", selectedAccount);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchResults = async (searchQuery: string, autoSelectFirst = false) => {
    if (!searchQuery) return;
    setLoading(true);
    try {
      const response = await axios.get<any>(LAMBDA_BASE_URL, {
        params: { searchAccount: searchQuery },
      });
      setResults(response.data);
      if (autoSelectFirst && response.data && response.data.length > 0) {
        onChange(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchResults = useCallback(
    debounce((query: string) => {
      fetchResults(query, false);
    }, 500),
    []
  );

  useEffect(() => {
    if (accountNameFromEmail) {
      fetchResults(accountNameFromEmail, true);
      setInputValue(accountNameFromEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNameFromEmail]);

  const handleInputChange = (_: any, value: string) => {
    setInputValue(value);
    debouncedFetchResults(value);
  };

  const handleOptionSelect = (_: any, value: any) => {
    if (!value) return;

    if (value.id === NEW_ACCOUNT_ID) {
      setShowConfirmModal(true);
      onChange({
        Account_Name: inputValue.trim(),
        id: NEW_ACCOUNT_ID,
        Website: null,
      });
    } else {
      onChange(value);
    }
  };

  const filterOptions = useCallback(
    (options: ZohoAccount[]) => {
      const trimmed = inputValue.trim();

      if (loading || !trimmed) return [];

      return [
        {
          Account_Name: `+ Create new account "${trimmed}"`,
          id: NEW_ACCOUNT_ID,
          Website: "Only account name is required",
        },
        ...options,
      ];
    },
    [inputValue, loading]
  );

  const onClose = () => {
    setShowConfirmModal(false);
    setInputValue("");
    onChange(null);
  };
  return (
    <>
      <Autocomplete
        clearIcon
        clearOnBlur
        inputValue={inputValue}
        value={selectedAccount}
        freeSolo
        loading={loading}
        options={results}
        filterOptions={filterOptions}
        getOptionLabel={(option: any) => option.Account_Name}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        renderOption={(props, option: any) => (
          <li {...props} key={option.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: 12,
              }}
            >
              <span>{option.Account_Name}</span>

              {option.Website && (
                <span
                  style={{
                    color: "#9e9e9e",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.Website}
                </span>
              )}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            variant='outlined'
            fullWidth
            placeholder='Account *'
          />
        )}
      />

      <Dialog open={showConfirmModal} onClose={onClose}>
        <DialogTitle>Confirm new account</DialogTitle>
        <DialogContent>
          Are you sure you want to create a new account:
          <br />
          <strong>"{inputValue.trim()}"</strong>?
          <br />
          <br />
          Make sure this account doesn't already exist.
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => setShowConfirmModal(false)}
          >
            Create account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountSearchBox;
